import Node from 'nanogl-node'

import { RunnerActor } from '@/state/RunnerStateMachine'
import { RenderContext } from '@/webgl/core/RenderContext'
import DebugDraw from '@/webgl/dev/debugDraw/DebugDraw'
import { IScene } from '@/webgl/engine/IScene'
import Renderer from '@/webgl/Renderer'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import RunnerCamera from '@/webgl/runner/RunnerCamera'
import ScenarioAudio from '@/webgl/runner/scenarios/ScenarioAudio'
import { AudioSegment } from '@/webgl/runner/scenarios/segments/AudioSegment'
import NarniaDoorSegment from '@/webgl/runner/scenarios/segments/NarniaDoorSegment'
import { Outro } from '@/webgl/runner/scenarios/segments/Outro'
import { PlaySegment } from '@/webgl/runner/scenarios/segments/PlaySegment'
import { Popin } from '@/webgl/runner/scenarios/segments/Popin'
import { TransitionSegment } from '@/webgl/runner/scenarios/segments/TransitionSegment'
import RunnerRenderer from '@/webgl/runner/SceneRenderer'
import PostprocessSystem from '@/webgl/runner/systems/PostprocessSystem'
import SequencingSystem from '@/webgl/runner/systems/SequencingSystem'

import Countdown from '../segments/Countdown'
import { Intro } from '../segments/Intro'
import Tooltip from '../segments/Tooltip'
import SETUP from './Pedro2Setup'

export default class Pedro2Scene implements IScene {
  glRenderer: Renderer
  root: Node
  camCtrl: RunnerCamera
  world: GameWorld
  renderer: IRunnerRenderer
  audio: ScenarioAudio

  constructor(renderer: Renderer) {
    this.glRenderer = renderer

    this.root = new Node()
    this.world = new GameWorld(this.root)
    this.camCtrl = new RunnerCamera(this.world)
    this.renderer = new RunnerRenderer(renderer, this.world, SETUP)

    this.world.getTrackingSystem().useDomElement(this.glRenderer.ilayer)

    this.world.registry.init(SETUP)
    this.world.systems.add(new PostprocessSystem(this.world, this.glRenderer.postprocess))

    this.audio = new ScenarioAudio('en', SETUP.audio, SETUP.audioSfx)

    this.world.background.spawnableItems = SETUP.sequences[0].scrolling

    const sequencing = new SequencingSystem(this.world)
    this.world.systems.add(sequencing)

    sequencing
      .init(this.renderer, SETUP)
      .add(new Intro('introduction'))
      .add(new AudioSegment('audio_intro', SETUP.sequences[0].scrolling))
      .add(new Popin('popin1', true))
      .add(new AudioSegment('audio_teacher', SETUP.sequences[0].scrolling))
      .add(new Countdown('countdown', 3))
      .add(new Tooltip('move'))
      .add(new Tooltip('pause'))
      .add(new PlaySegment('phase_1'))
      .add(new TransitionSegment('transition_1'))
      .add(new Popin('popin2'))
      .add(new AudioSegment('audio_clown'))
      .add(new PlaySegment('phase_2'))
      .add(new TransitionSegment('transition_2'))
      .add(new Popin('popin3'))
      .add(new PlaySegment('phase_3'))
      .add(new PlaySegment('phase_4'))
      .add(new TransitionSegment('transition_4', { autoReopen: true }))
      .add(new Popin('popin4'))
      .add(new PlaySegment('phase_5'))
      .add(new TransitionSegment('transition_5', { autoReopen: true }))
      .add(new Popin('popin5'))
      .add(new AudioSegment('audio_food'))
      .add(new PlaySegment('phase_6'))
      .add(new NarniaDoorSegment('narnia_door'))
      .add(new AudioSegment('audio_narnia'))
      .add(new Outro('outro'))

    /// #if _DEBUG
    const params = new URLSearchParams(window.location.search)
    if (params.get('segment')) {
      sequencing.setStartSegmentId(params.get('segment'))
    }
    /// #endif
  }

  public setActor(actor: RunnerActor) {
    this.world.state.setActor(actor)
  }

  async load(abort: AbortSignal): Promise<void> {
    await Promise.all([
      this.renderer.load(abort),
      this.world.systems.get<SequencingSystem>(SequencingSystem).load(abort),
      this.audio.load(abort),
    ])
  }

  start() {
    this.glRenderer.cameras.getManager('main').setControler(this.camCtrl)

    this.world.state.setJauge(0)
    this.world.start()
  }

  preRender(): void {
    this.world.update(this.glRenderer.dt)
    const time = this.world.time
    this.renderer.preRender(time)
    this.root.updateWorldMatrix()
  }

  rttPass(context: RenderContext): void {
    this.renderer.rttPass(context)
  }

  render(context: RenderContext): void {
    this.renderer.render(context)
    this.world.systems.forEach((system) => system.render?.(context))

    if (this.camCtrl.camera) DebugDraw.drawFrustum(this.camCtrl.camera._viewProj)
  }

  dispose(): void {
    this.world.dispose()
    this.audio.dispose()
  }
}
